import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
  Stack,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDateInNewfoundland } from "../../../utils/dates/formatDateInNewfoundland";
import dayjs from "dayjs";

const statusColors = {
  new: "primary",
  read: "info",
  responded: "success",
  archived: "default",
};

const isVisuallyNew = (msg) =>
  msg.status === "new" &&
  dayjs(msg.submissionDate || msg.createdAt).isAfter(
    dayjs().subtract(3, "day")
  );

const ContactMessagesAccordion = ({
  messages,
  onStatusUpdate,
  onRequestDelete,
  statusOptions,
  expandedAccordionId,
  setExpandedAccordionId,
  sortConfig,
  setSortConfig,
}) => {
  return (
    <Box sx={{ overflowX: "hidden" }}>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          value={`${sortConfig.field}:${sortConfig.direction}`}
          onChange={(e) => {
            const [field, direction] = e.target.value.split(":");
            setSortConfig({ field, direction });
          }}>
          <MenuItem value="submissionDate:desc">Newest First</MenuItem>
          <MenuItem value="submissionDate:asc">Oldest First</MenuItem>
          <MenuItem value="firstName:asc">Name (A–Z)</MenuItem>
          <MenuItem value="firstName:desc">Name (Z–A)</MenuItem>
          <MenuItem value="status:asc">Status (A–Z)</MenuItem>
          <MenuItem value="status:desc">Status (Z–A)</MenuItem>
        </Select>
      </FormControl>

      {messages.map((msg) => (
        <Accordion
          key={msg._id}
          expanded={expandedAccordionId === msg._id}
          onChange={(_, isExpanded) => {
            setExpandedAccordionId(isExpanded ? msg._id : null);
            if (isExpanded && msg.status === "new") {
              onStatusUpdate(msg._id, "read");
            }
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "#fafafa",
              borderBottom: "1px solid #e0e0e0",
              borderRadius: "8px",
              px: 2,
              py: 1.5,
              "& .MuiAccordionSummary-content": { margin: 0 },
            }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography fontWeight="bold">
                {msg.firstName} {msg.lastName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: "break-word" }}>
                {msg.email}
              </Typography>

              <Stack direction="row" spacing={1} mt={1}>
                <Chip
                  label={msg.status}
                  color={statusColors[msg.status] || "default"}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "capitalize",
                  }}
                />
                {isVisuallyNew(msg) && (
                  <Chip
                    label="new"
                    color="primary"
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "capitalize",
                    }}
                  />
                )}
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pb: 3 }}>
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <Select
                  value={msg.status}
                  onChange={(e) => onStatusUpdate(msg._id, e.target.value)}>
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary">
                Phone: {msg.phoneNumber || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted:{" "}
                {formatDateInNewfoundland(
                  msg.submissionDate || msg.createdAt,
                  "MMM D, YYYY h:mm A"
                )}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Message</Typography>
              <Typography whiteSpace="pre-wrap">{msg.message}</Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => onRequestDelete(msg._id)}>
                  Delete
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ContactMessagesAccordion;
